import { EnvironmentOutlined } from "@ant-design/icons";
import {
  Button,
  Empty,
  Space,
  Spin,
  Tag,
  Timeline,
  Tooltip,
  Typography,
} from "antd";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { FormattedMessage } from "react-intl";

import { colors } from "../../../lib/constants/colors";
import { formatRevertReason } from "../../../lib/formats/changeLog";
import { statusColor } from "../../../lib/formats/colors";
import { humanize } from "../../../lib/formats/common";
import { formatDateTime } from "../../../lib/formats/dates";
import { formatUsername } from "../../../lib/formats/users";
import type { ChangeLogFragment } from "../../../lib/graphql";
import { useChangeLogs } from "../../../lib/hooks/basic/changeLogs";
import { Avatar } from "../Avatar";

interface ChangeListProps {
  id: string;
  type: string;
}

function eventColor(event: string) {
  switch (event) {
    case "created":
      return "green";
    case "updated":
      return "blue";
    default:
      return statusColor(event);
  }
}

const excludeKeys = ["tenant_id", "updated_at"];

function ChangeOrigin({ origin }: { origin: ChangeLogFragment["origin"] }) {
  if (!origin?.latitude || !origin?.longitude) return null;

  const client = [origin.client, origin.version].filter((p) => p).join(" ");

  return (
    <Typography.Text type="secondary" style={{ fontSize: "12px" }}>
      {" "}
      <Tooltip title={client || undefined}>
        <a
          href={`https://www.google.com/maps?q=${origin.latitude},${origin.longitude}`}
          target="_blank"
          rel="noreferrer"
        >
          <EnvironmentOutlined />
        </a>
      </Tooltip>
      {origin.accuracy != null && ` ±${Math.round(origin.accuracy)} m`}
    </Typography.Text>
  );
}

export function formatChanges(changes: any, key?: any): any {
  if (changes == null) return null;

  if (key === "reason" && typeof changes === "string") {
    return formatRevertReason(changes);
  }

  if (Array.isArray(changes)) {
    if (
      (changes[0] == null || typeof changes[0] !== "object") &&
      (changes[1] == null || typeof changes[1] !== "object")
    ) {
      const color =
        changes[0] == null || key === "add"
          ? colors.successColor
          : changes[1] == null || key === "remove"
            ? colors.dangerColor
            : undefined;
      const delimiter = ["add", "remove"].includes(key) ? ", " : " → ";
      return (
        <span style={{ color }}>
          {changes
            .filter((c) => c != null)
            .map(formatChanges)
            .join(delimiter)}
        </span>
      );
    } else {
      return changes.map((c) => formatChanges(c, key));
    }
  } else if (typeof changes === "number") {
    return String(changes);
  } else if (!Number.isNaN(Date.parse(changes)) && changes.length == 29) {
    return dayjs(changes).format("L LT");
  } else if (typeof changes === "boolean" || typeof changes === "string") {
    return String(changes);
  } else {
    return Object.entries(changes).map(([key, value]) =>
      excludeKeys.includes(key) ? null : (
        <div key={key}>
          {humanize(key)}:
          <div style={{ paddingLeft: "10px", borderLeft: "1px dashed #ccc" }}>
            {formatChanges(value, key)}
          </div>
        </div>
      )
    );
  }
}

export function ChangeList({ id, type }: ChangeListProps) {
  let page = 1;

  const { items, totalCount, loading, refetch } = useChangeLogs({
    variables: {
      filter: { objId: Number(id), objType: type },
      page,
    },
    fetchPolicy: "no-cache",
  });

  const [changes, setChanges] = useState<ChangeLogFragment[]>([]);

  useEffect(() => {
    if (items && items.length > 0) {
      setChanges((c) => c.concat(items));
    }
  }, [items]);

  if (loading && changes.length === 0) {
    return (
      <div style={{ textAlign: "center", marginTop: "40vh" }}>
        <Spin />
      </div>
    );
  }

  if (!items || items.length === 0) return <Empty />;

  return (
    <>
      <Timeline
        items={changes.map((change) => ({
          key: change.id,
          color: eventColor(change.event),
          children: (
            <>
              <Space>
                <span>{formatDateTime(change.createdAt)}</span>
                <Tag color={eventColor(change.event)}>
                  {humanize(change.event)}
                </Tag>
              </Space>
              <div>
                <Typography.Text type="secondary" style={{ fontSize: "12px" }}>
                  <Avatar user={change.user} size={14} />{" "}
                  {formatUsername(change.user)}
                  {change.impersonator && (
                    <Typography.Text
                      italic
                      type="secondary"
                      style={{ fontSize: "12px" }}
                    >
                      {" "}
                      <FormattedMessage
                        id="impersonation.changeLog.via"
                        defaultMessage="via"
                      />{" "}
                      <Avatar user={change.impersonator} size={14} />{" "}
                      {formatUsername(change.impersonator)}
                    </Typography.Text>
                  )}
                  <ChangeOrigin origin={change.origin} />
                </Typography.Text>
              </div>
              <div>{formatChanges(change.objectChanges)}</div>
            </>
          ),
        }))}
      />

      {totalCount && totalCount > changes.length && (
        <div style={{ textAlign: "right" }}>
          <Button
            type="primary"
            onClick={() => {
              refetch && refetch({ page: ++page });
            }}
          >
            <FormattedMessage id="next" defaultMessage="Next" />
          </Button>
        </div>
      )}
    </>
  );
}
